import React, { useState, useMemo, useCallback } from 'react';
import {
    TopologyData,
    BaselineData,
    LatencyPath,
    SiteLatencyPath,
    SiteLatencyResult,
    usToMs,
    getLatencyColor,
    formatLoss,
    getLossColor,
    formatMbps,
} from '../TopologyData';
import {
    computeLatencyPaths,
    computeSiteLatencyPaths,
} from '../utils/pathfinder';

export type AnalysisMode = 'site' | 'router';

interface Props {
    data: TopologyData;
    baseline: BaselineData | null;
    initialSource?: string | null;
    initialTarget?: string | null;
    initialSiteSrc?: string | null;
    initialSiteTgt?: string | null;
    mode?: AnalysisMode;
}

const ModeBtn: React.FC<{
    active: boolean;
    onClick: () => void;
    children: React.ReactNode;
}> = ({ active, onClick, children }) => (
    <button
        onClick={onClick}
        className={`px-4 py-1.5 text-xs font-semibold rounded-md
            transition-colors ${
                active
                    ? 'bg-semantic-bg-primary text-semantic-text-primary shadow-sm'
                    : 'text-semantic-text-secondary hover:text-semantic-text-primary'
            }`}
    >
        {children}
    </button>
);

const selectClass =
    'px-3 py-2 text-sm rounded-lg border border-semantic-border-secondary bg-semantic-bg-primary text-semantic-text-primary focus:outline-none focus:ring-2 focus:ring-semantic-border-brand min-w-[220px]';

const LatencyPathAnalysis: React.FC<Props> = ({
    data,
    initialSource,
    initialTarget,
    initialSiteSrc,
    initialSiteTgt,
    mode: controlledMode,
}) => {
    const [internalMode, setMode] = useState<AnalysisMode>('site');
    const mode = controlledMode ?? internalMode;

    // Router mode state
    const [routerSrc, setRouterSrc] = useState<string>(initialSource ?? '');
    const [routerTgt, setRouterTgt] = useState<string>(initialTarget ?? '');

    // Site mode state
    const [siteSrc, setSiteSrc] = useState<string>('');
    const [siteTgt, setSiteTgt] = useState<string>('');

    const [expandedPath, setExpandedPath] = useState<number | null>(null);

    // Sync from props — router path select
    React.useEffect(() => {
        if (initialSource) {
            setRouterSrc(initialSource);
            setMode('router');
        }
    }, [initialSource]);
    React.useEffect(() => {
        if (initialTarget) {
            setRouterTgt(initialTarget);
            setMode('router');
        }
    }, [initialTarget]);

    // Sync from props — site path select
    React.useEffect(() => {
        if (initialSiteSrc) {
            setSiteSrc(initialSiteSrc);
            setMode('site');
        }
    }, [initialSiteSrc]);
    React.useEffect(() => {
        if (initialSiteTgt) {
            setSiteTgt(initialSiteTgt);
            setMode('site');
        }
    }, [initialSiteTgt]);

    const sortedNodes = useMemo(
        () => [...data.nodes].sort((a, b) => a.id.localeCompare(b.id)),
        [data.nodes]
    );

    const sortedSites = useMemo(
        () =>
            [...data.topology.sites].sort((a, b) =>
                a.id.localeCompare(b.id)
            ),
        [data.topology.sites]
    );

    // Router-to-Router paths
    const routerPaths = useMemo<LatencyPath[]>(() => {
        if (mode !== 'router' || !routerSrc || !routerTgt || routerSrc === routerTgt)
            return [];
        return computeLatencyPaths(data.links, routerSrc, routerTgt);
    }, [data.links, routerSrc, routerTgt, mode]);

    // Site-to-Site result
    const siteResult = useMemo<SiteLatencyResult | null>(() => {
        if (mode !== 'site' || !siteSrc || !siteTgt || siteSrc === siteTgt)
            return null;
        return computeSiteLatencyPaths(
            data.links,
            data.topology.sites,
            siteSrc,
            siteTgt
        );
    }, [data.links, data.topology.sites, siteSrc, siteTgt, mode]);

    // Build link throughput lookup: linkId -> max throughput in Mbps
    const linkThroughput = useMemo(() => {
        const map = new Map<string, number>();
        for (const link of data.links) {
            const fIn = link.forward?.inboundMbps ?? 0;
            const fOut = link.forward?.outboundMbps ?? 0;
            const rIn = link.reverse?.inboundMbps ?? 0;
            const rOut = link.reverse?.outboundMbps ?? 0;
            map.set(link.id, Math.max(fIn, fOut, rIn, rOut));
        }
        return map;
    }, [data.links]);

    const pathTraffic = useCallback(
        (path: LatencyPath) => {
            let total = 0;
            for (const hop of path.hops) {
                total += linkThroughput.get(hop.linkId) ?? 0;
            }
            return total / Math.max(path.hopCount, 1);
        },
        [linkThroughput]
    );

    const directPath = routerPaths.find((p) => p.isDirect);

    const handleSwap = () => {
        if (mode === 'router') {
            setRouterSrc(routerTgt);
            setRouterTgt(routerSrc);
        } else {
            setSiteSrc(siteTgt);
            setSiteTgt(siteSrc);
        }
        setExpandedPath(null);
    };

    const handleModeChange = (m: AnalysisMode) => {
        setMode(m);
        setExpandedPath(null);
    };

    // --- Filters & sorting ---
    type CarrierFilter = 'all' | 'single' | 'multi';
    type SortBy = 'rtt-asc' | 'rtt-desc' | 'hops-asc' | 'hops-desc' | 'loss-desc' | 'loss-asc';

    const [carrierFilter, setCarrierFilter] =
        useState<CarrierFilter>('all');
    const [sortBy, setSortBy] = useState<SortBy>('rtt-asc');

    const MAX_PATHS_ROUTER = 20;
    const MAX_PATHS_SITE = 30;

    // Shared vars for current mode
    const currentSource = mode === 'router' ? routerSrc : siteSrc;
    const currentTarget = mode === 'router' ? routerTgt : siteTgt;
    const rawPaths: (LatencyPath | SiteLatencyPath)[] =
        mode === 'router' ? routerPaths : (siteResult?.paths ?? []);

    // Helper: get unique carriers for a path
    const getPathCarriers = (p: LatencyPath) =>
        Array.from(
            new Set(
                p.hops
                    .map((h) => h.carrier)
                    .filter((c): c is string => c != null)
            )
        );

    // Apply filters, sort, and limit
    const currentPaths = useMemo(() => {
        let filtered = rawPaths;

        // Carrier filter
        if (carrierFilter !== 'all') {
            filtered = filtered.filter((p) => {
                const carriers = getPathCarriers(p);
                if (carriers.length === 0) return false;
                return carrierFilter === 'single'
                    ? carriers.length === 1
                    : carriers.length > 1;
            });
        }

        // Sort
        const sorted = [...filtered].sort((a, b) => {
            switch (sortBy) {
                case 'rtt-asc':
                    return a.rttUs - b.rttUs;
                case 'rtt-desc':
                    return b.rttUs - a.rttUs;
                case 'hops-asc':
                    return a.hopCount - b.hopCount || a.rttUs - b.rttUs;
                case 'hops-desc':
                    return b.hopCount - a.hopCount || a.rttUs - b.rttUs;
                case 'loss-desc': {
                    const aLossD = Math.max(
                        ...a.hops.map((h) => h.lossPercent ?? 0)
                    );
                    const bLossD = Math.max(
                        ...b.hops.map((h) => h.lossPercent ?? 0)
                    );
                    return bLossD - aLossD || a.rttUs - b.rttUs;
                }
                case 'loss-asc': {
                    const aLossA = Math.max(
                        ...a.hops.map((h) => h.lossPercent ?? 0)
                    );
                    const bLossA = Math.max(
                        ...b.hops.map((h) => h.lossPercent ?? 0)
                    );
                    return aLossA - bLossA || a.rttUs - b.rttUs;
                }
                default:
                    return 0;
            }
        });

        // Limit
        const max =
            mode === 'router' ? MAX_PATHS_ROUTER : MAX_PATHS_SITE;
        return sorted.slice(0, max);
    }, [rawPaths, carrierFilter, sortBy, mode]);

    const totalBeforeLimit = rawPaths.filter((p) => {
        if (carrierFilter === 'all') return true;
        const carriers = getPathCarriers(p);
        if (carriers.length === 0) return false;
        return carrierFilter === 'single'
            ? carriers.length === 1
            : carriers.length > 1;
    }).length;

    return (
        <div className="flex flex-col gap-4 h-full overflow-auto p-4">
            {!controlledMode && (
                <div className="flex items-center gap-3">
                    <div className="flex gap-1 p-1 bg-semantic-bg-secondary rounded-lg w-fit">
                        <ModeBtn
                            active={mode === 'site'}
                            onClick={() => handleModeChange('site')}
                        >
                            Site to Site
                        </ModeBtn>
                        <ModeBtn
                            active={mode === 'router'}
                            onClick={() => handleModeChange('router')}
                        >
                            Router to Router
                        </ModeBtn>
                    </div>
                </div>
            )}

            {/* Selectors */}
            <div className="flex items-end gap-3 flex-wrap">
                <div className="flex flex-col gap-1">
                    <label className="text-xs font-semibold text-semantic-text-secondary uppercase tracking-wide">
                        {mode === 'site' ? 'Source Site' : 'Source Node'}
                    </label>
                    {mode === 'router' ? (
                        <select
                            value={routerSrc}
                            onChange={(e) => {
                                setRouterSrc(e.target.value);
                                setExpandedPath(null);
                            }}
                            className={selectClass}
                        >
                            <option value="">Select source...</option>
                            {sortedNodes.map((n) => (
                                <option key={n.id} value={n.id}>
                                    {n.hostname} ({n.location.datacenterId || n.location.label})
                                </option>
                            ))}
                        </select>
                    ) : (
                        <select
                            value={siteSrc}
                            onChange={(e) => {
                                setSiteSrc(e.target.value);
                                setExpandedPath(null);
                            }}
                            className={selectClass}
                        >
                            <option value="">Select source site...</option>
                            {sortedSites.map((s) => (
                                <option key={s.id} value={s.id}>
                                    {s.location.datacenterId || s.id} ({s.location.city}, {s.location.country}) ({s.nodes.length} nodes)
                                </option>
                            ))}
                        </select>
                    )}
                </div>

                <button
                    onClick={handleSwap}
                    disabled={!currentSource && !currentTarget}
                    className="px-3 py-2 text-sm rounded-lg border border-semantic-border-secondary
                        text-semantic-text-secondary hover:text-semantic-text-primary
                        hover:bg-semantic-bg-secondary transition-colors
                        disabled:opacity-30 disabled:cursor-not-allowed"
                    title="Swap source and target"
                >
                    &#x21C4;
                </button>

                <div className="flex flex-col gap-1">
                    <label className="text-xs font-semibold text-semantic-text-secondary uppercase tracking-wide">
                        {mode === 'site'
                            ? 'Destination Site'
                            : 'Destination Node'}
                    </label>
                    {mode === 'router' ? (
                        <select
                            value={routerTgt}
                            onChange={(e) => {
                                setRouterTgt(e.target.value);
                                setExpandedPath(null);
                            }}
                            className={selectClass}
                        >
                            <option value="">Select destination...</option>
                            {sortedNodes.map((n) => (
                                <option key={n.id} value={n.id}>
                                    {n.hostname} ({n.location.datacenterId || n.location.label})
                                </option>
                            ))}
                        </select>
                    ) : (
                        <select
                            value={siteTgt}
                            onChange={(e) => {
                                setSiteTgt(e.target.value);
                                setExpandedPath(null);
                            }}
                            className={selectClass}
                        >
                            <option value="">
                                Select destination site...
                            </option>
                            {sortedSites.map((s) => (
                                <option key={s.id} value={s.id}>
                                    {s.location.datacenterId || s.id} ({s.location.city}, {s.location.country}) ({s.nodes.length} nodes)
                                </option>
                            ))}
                        </select>
                    )}
                </div>

                {currentSource &&
                    currentTarget &&
                    currentSource === currentTarget && (
                        <span className="text-xs text-semantic-text-warning font-medium py-2">
                            Source and destination must be different
                        </span>
                    )}
            </div>

            {/* Site summary card */}
            {mode === 'site' && siteResult && (
                <div
                    className="flex items-center gap-6 px-4 py-3 rounded-lg
                        border border-semantic-border-brand bg-semantic-bg-brand-primary"
                >
                    <div className="text-xs font-bold uppercase tracking-wide text-semantic-text-brand">
                        {siteResult.sourceLocation.datacenterId || siteResult.sourceSite}
                        {' '}({siteResult.sourceLocation.city}, {siteResult.sourceLocation.country})
                        {' '}&rarr;{' '}
                        {siteResult.targetLocation.datacenterId || siteResult.targetSite}
                        {' '}({siteResult.targetLocation.city}, {siteResult.targetLocation.country})
                    </div>
                    <StatPill
                        label="Best RTT"
                        value={usToMs(siteResult.bestRttUs)}
                        color="var(--color-semantic-text-success)"
                    />
                    <StatPill
                        label="Worst RTT"
                        value={usToMs(siteResult.worstRttUs)}
                        color="var(--color-semantic-text-danger)"
                    />
                    <div className="text-xs text-semantic-text-secondary">
                        {siteResult.totalPaths} paths
                    </div>
                </div>
            )}

            {/* Router direct connection indicator */}
            {mode === 'router' && directPath && (
                <div
                    className="flex items-center gap-3 px-4 py-3 rounded-lg
                        border border-semantic-border-success bg-semantic-bg-success-primary"
                >
                    <span className="text-xs font-bold uppercase tracking-wide text-semantic-text-success">
                        Direct Link
                    </span>
                    <span className="text-sm text-semantic-text-primary font-semibold">
                        RTT: {usToMs(directPath.rttUs)}
                    </span>
                    <span className="text-xs text-semantic-text-secondary">
                        Fwd: {usToMs(directPath.forwardLatencyUs)} | Rev:{' '}
                        {usToMs(directPath.reverseLatencyUs)}
                    </span>
                    <span className="text-xs text-semantic-text-secondary">
                        | {directPath.hops[0]?.bandwidth}
                    </span>
                </div>
            )}

            {/* Filters bar */}
            {rawPaths.length > 0 && (
                <div className="flex items-center gap-4 flex-wrap">
                    {/* Carrier filter */}
                    <div className="flex items-center gap-1.5">
                        <span className="text-[10px] font-semibold text-semantic-text-secondary uppercase tracking-wide">
                            Carrier
                        </span>
                        <div className="flex gap-0.5 p-0.5 bg-semantic-bg-secondary rounded-md">
                            {(
                                [
                                    ['all', 'All'],
                                    ['single', 'Single'],
                                    ['multi', 'Multi'],
                                ] as const
                            ).map(([val, label]) => (
                                <button
                                    key={val}
                                    onClick={() => {
                                        setCarrierFilter(val);
                                        setExpandedPath(null);
                                    }}
                                    className={`px-2.5 py-1 text-[10px] font-semibold rounded
                                        transition-colors ${
                                            carrierFilter === val
                                                ? 'bg-semantic-bg-primary text-semantic-text-primary shadow-sm'
                                                : 'text-semantic-text-secondary hover:text-semantic-text-primary'
                                        }`}
                                >
                                    {label}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Sort */}
                    <div className="flex items-center gap-1.5">
                        <span className="text-[10px] font-semibold text-semantic-text-secondary uppercase tracking-wide">
                            Sort
                        </span>
                        <select
                            value={sortBy}
                            onChange={(e) => {
                                setSortBy(
                                    e.target.value as SortBy
                                );
                                setExpandedPath(null);
                            }}
                            className="px-2 py-1 text-xs rounded-md border border-semantic-border-secondary
                                bg-semantic-bg-primary text-semantic-text-primary
                                focus:outline-none focus:ring-1 focus:ring-semantic-border-brand"
                        >
                            <option value="rtt-asc">
                                RTT: Low → High
                            </option>
                            <option value="rtt-desc">
                                RTT: High → Low
                            </option>
                            <option value="hops-asc">
                                Hops: Fewest first
                            </option>
                            <option value="hops-desc">
                                Hops: Most first
                            </option>
                            <option value="loss-desc">
                                Loss: High → Low
                            </option>
                            <option value="loss-asc">
                                Loss: Low → High
                            </option>
                        </select>
                    </div>

                    {/* Results count */}
                    <span className="text-xs text-semantic-text-secondary ml-auto">
                        {currentPaths.length === totalBeforeLimit
                            ? `${currentPaths.length} paths`
                            : `${currentPaths.length} of ${totalBeforeLimit} paths (max ${mode === 'router' ? MAX_PATHS_ROUTER : MAX_PATHS_SITE})`}
                    </span>
                </div>
            )}

            {/* No results */}
            {currentSource &&
                currentTarget &&
                currentSource !== currentTarget &&
                currentPaths.length === 0 && (
                    <div className="text-xs text-semantic-text-secondary">
                        {rawPaths.length === 0
                            ? 'No paths found'
                            : 'No paths match the current filters'}
                    </div>
                )}

            {/* Paths table */}
            {currentPaths.length > 0 && (
                <div className="rounded-lg border border-semantic-border-secondary overflow-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="bg-semantic-bg-secondary text-semantic-text-secondary text-xs uppercase tracking-wide">
                                <th className="px-3 py-2.5 text-left w-10">
                                    #
                                </th>
                                {mode === 'site' && (
                                    <>
                                        <th className="px-3 py-2.5 text-left w-36">
                                            Src Router
                                        </th>
                                        <th className="px-3 py-2.5 text-left w-36">
                                            Dst Router
                                        </th>
                                    </>
                                )}
                                <th className="px-3 py-2.5 text-left">Path</th>
                                <th className="px-3 py-2.5 text-center w-16">
                                    Hops
                                </th>
                                <th className="px-3 py-2.5 text-right w-28">
                                    RTT
                                </th>
                                <th className="px-3 py-2.5 text-right w-28">
                                    Forward
                                </th>
                                <th className="px-3 py-2.5 text-right w-28">
                                    Reverse
                                </th>
                                <th className="px-3 py-2.5 text-right w-24">
                                    Max BW
                                </th>
                                <th className="px-3 py-2.5 text-right w-28">
                                    Avg Traffic
                                </th>
                                <th className="px-3 py-2.5 text-right w-24">
                                    Packet Loss
                                </th>
                                <th className="px-3 py-2.5 text-left w-32">
                                    Carriers
                                </th>
                                <th className="px-3 py-2.5 text-center w-20">
                                    Status
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {currentPaths.map((path, idx) => {
                                const sitePath =
                                    mode === 'site'
                                        ? (path as SiteLatencyPath)
                                        : null;
                                const isExpanded = expandedPath === idx;
                                return (
                                    <React.Fragment key={idx}>
                                        <tr
                                            className={`border-t border-semantic-border-secondary
                                                cursor-pointer transition-colors group
                                                ${isExpanded ? 'bg-semantic-bg-secondary' : 'hover:bg-semantic-bg-secondary/50'}
                                                ${path.isBest && !isExpanded ? 'bg-semantic-bg-success-primary/30' : ''}`}
                                            onClick={() =>
                                                setExpandedPath(
                                                    isExpanded
                                                        ? null
                                                        : idx
                                                )
                                            }
                                        >
                                            <td className="px-3 py-2.5 text-semantic-text-secondary font-mono">
                                                <span className="flex items-center gap-1.5">
                                                    <svg
                                                        className={`w-3 h-3 transition-transform duration-200 text-semantic-text-secondary/60 group-hover:text-semantic-text-primary
                                                            ${isExpanded ? 'rotate-90' : ''}`}
                                                        viewBox="0 0 16 16"
                                                        fill="currentColor"
                                                    >
                                                        <path d="M6 3l5 5-5 5V3z" />
                                                    </svg>
                                                    {idx + 1}
                                                </span>
                                            </td>
                                            {sitePath && (
                                                <>
                                                    <td className="px-3 py-2.5 font-mono text-xs">
                                                        {shortName(
                                                            sitePath.sourceRouter
                                                        )}
                                                    </td>
                                                    <td className="px-3 py-2.5 font-mono text-xs">
                                                        {shortName(
                                                            sitePath.targetRouter
                                                        )}
                                                    </td>
                                                </>
                                            )}
                                            <td className="px-3 py-2.5">
                                                <PathDisplay path={path} />
                                            </td>
                                            <td className="px-3 py-2.5 text-center font-mono">
                                                {path.hopCount}
                                            </td>
                                            <td className="px-3 py-2.5 text-right font-semibold">
                                                <span
                                                    style={{
                                                        color: getLatencyColor(
                                                            path.rttUs / 2
                                                        ),
                                                    }}
                                                >
                                                    {usToMs(path.rttUs)}
                                                </span>
                                            </td>
                                            <td className="px-3 py-2.5 text-right font-mono text-semantic-text-secondary">
                                                {usToMs(
                                                    path.forwardLatencyUs
                                                )}
                                            </td>
                                            <td className="px-3 py-2.5 text-right font-mono text-semantic-text-secondary">
                                                {usToMs(
                                                    path.reverseLatencyUs
                                                )}
                                            </td>
                                            <td className="px-3 py-2.5 text-right font-mono text-xs text-semantic-text-secondary">
                                                {path.bottleneckBandwidth}
                                            </td>
                                            <td className="px-3 py-2.5 text-right font-mono text-xs text-semantic-text-secondary">
                                                {formatMbps(pathTraffic(path))}
                                            </td>
                                            <td className="px-3 py-2.5 text-right font-mono text-xs font-semibold"
                                                style={{
                                                    color: getLossColor(
                                                        Math.max(
                                                            ...path.hops.map(
                                                                (h) =>
                                                                    h.lossPercent ??
                                                                    0
                                                            )
                                                        )
                                                    ),
                                                }}
                                            >
                                                {formatLoss(
                                                    Math.max(
                                                        ...path.hops.map(
                                                            (h) =>
                                                                h.lossPercent ??
                                                                0
                                                        )
                                                    )
                                                )}
                                            </td>
                                            <td className="px-3 py-2.5">
                                                <CarrierBadges
                                                    path={path}
                                                />
                                            </td>
                                            <td className="px-3 py-2.5 text-center">
                                                {path.isBest && (
                                                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wide bg-semantic-bg-success-bold text-semantic-text-inverse-primary">
                                                        Best
                                                    </span>
                                                )}
                                                {path.isDirect &&
                                                    !path.isBest && (
                                                        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wide bg-semantic-bg-brand-secondary text-semantic-text-brand">
                                                            Direct
                                                        </span>
                                                    )}
                                            </td>
                                        </tr>

                                        {isExpanded && (
                                            <tr>
                                                <td
                                                    colSpan={
                                                        mode === 'site'
                                                            ? 13
                                                            : 11
                                                    }
                                                    className="p-0"
                                                >
                                                    <div className="mx-3 my-2 rounded-lg border border-semantic-border-secondary bg-semantic-bg-primary overflow-hidden">
                                                        <div className="px-4 py-2 bg-semantic-bg-secondary/50 border-b border-semantic-border-secondary flex items-center justify-between">
                                                            <span className="text-[10px] font-bold uppercase tracking-widest text-semantic-text-secondary">
                                                                Hop-by-Hop Detail
                                                            </span>
                                                            <span className="text-[10px] text-semantic-text-secondary">
                                                                {path.hopCount} hop{path.hopCount !== 1 ? 's' : ''}
                                                            </span>
                                                        </div>
                                                        <div className="p-3">
                                                            <HopDetail path={path} linkThroughput={linkThroughput} />
                                                        </div>
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Empty state */}
            {!currentSource && !currentTarget && (
                <div className="flex-1 flex items-center justify-center text-semantic-text-secondary text-sm">
                    {mode === 'site'
                        ? 'Select a source and destination site to analyze latency paths'
                        : 'Select a source and destination node to analyze latency paths'}
                </div>
            )}
        </div>
    );
};

/** Stat pill for site summary */
const StatPill: React.FC<{
    label: string;
    value: string;
    color?: string;
}> = ({ label, value, color }) => (
    <div className="flex flex-col items-center">
        <span className="text-[10px] text-semantic-text-secondary uppercase">
            {label}
        </span>
        <span
            className="text-sm font-bold"
            style={{ color: color ?? 'var(--color-semantic-text-primary)' }}
        >
            {value}
        </span>
    </div>
);

/** Unique carrier badges for a path */
const CarrierBadges: React.FC<{ path: LatencyPath }> = ({ path }) => {
    const carriers = Array.from(
        new Set(
            path.hops
                .map((h) => h.carrier)
                .filter((c): c is string => c != null)
        )
    );
    if (carriers.length === 0) {
        return (
            <span className="text-semantic-text-secondary text-xs">—</span>
        );
    }
    return (
        <span className="flex gap-1 flex-wrap">
            {carriers.map((c) => (
                <span
                    key={c}
                    className="text-[10px] font-semibold px-1.5 py-0.5
                        rounded-full bg-semantic-bg-brand-secondary
                        text-semantic-text-brand"
                >
                    {c}
                </span>
            ))}
        </span>
    );
};

/** Short hostname: KE.MXMEX05.RT02 -> MXMEX05.RT02 */
function shortName(id: string): string {
    const parts = id.split('.');
    return parts.length >= 3
        ? `${parts[parts.length - 2]}.${parts[parts.length - 1]}`
        : id;
}

/** Compact path display: A -> B -> C */
const PathDisplay: React.FC<{ path: LatencyPath }> = ({ path }) => {
    if (path.hops.length === 0) return null;
    const nodeIds = [
        path.hops[0].from,
        ...path.hops.map((h) => h.to),
    ];

    return (
        <span className="flex items-center gap-1 flex-wrap text-xs">
            {nodeIds.map((n, i) => (
                <React.Fragment key={n + i}>
                    <span
                        className="font-mono font-medium text-semantic-text-primary"
                        title={n}
                    >
                        {shortName(n)}
                    </span>
                    {i < nodeIds.length - 1 && (
                        <span className="text-semantic-text-secondary">
                            &rarr;
                        </span>
                    )}
                </React.Fragment>
            ))}
        </span>
    );
};

/** Expanded hop-by-hop detail table */
const HopDetail: React.FC<{ path: LatencyPath; linkThroughput: Map<string, number> }> = ({ path, linkThroughput }) => (
    <table className="w-full text-xs">
        <thead>
            <tr className="text-semantic-text-secondary uppercase tracking-wide">
                <th className="px-2 py-1.5 text-left">Hop</th>
                <th className="px-2 py-1.5 text-left">From</th>
                <th className="px-2 py-1.5 text-left">To</th>
                <th className="px-2 py-1.5 text-left">Carrier</th>
                <th className="px-2 py-1.5 text-right">Forward</th>
                <th className="px-2 py-1.5 text-right">Reverse</th>
                <th className="px-2 py-1.5 text-right">RTT</th>
                <th className="px-2 py-1.5 text-right">Loss</th>
                <th className="px-2 py-1.5 text-right">Bandwidth</th>
                <th className="px-2 py-1.5 text-right">Traffic</th>
            </tr>
        </thead>
        <tbody>
            {path.hops.map((hop, i) => (
                <tr
                    key={hop.linkId + i}
                    className="border-t border-semantic-border-secondary/50"
                >
                    <td className="px-2 py-1.5 font-mono text-semantic-text-secondary">
                        {i + 1}
                    </td>
                    <td className="px-2 py-1.5 font-mono font-medium">
                        {hop.from}
                    </td>
                    <td className="px-2 py-1.5 font-mono font-medium">
                        {hop.to}
                    </td>
                    <td className="px-2 py-1.5">
                        {hop.carrier ? (
                            <span
                                className="text-[10px] font-semibold px-1.5 py-0.5
                                    rounded-full bg-semantic-bg-brand-secondary
                                    text-semantic-text-brand"
                            >
                                {hop.carrier}
                            </span>
                        ) : (
                            <span className="text-semantic-text-secondary">
                                —
                            </span>
                        )}
                    </td>
                    <td className="px-2 py-1.5 text-right font-mono">
                        {usToMs(hop.forwardLatencyUs)}
                    </td>
                    <td className="px-2 py-1.5 text-right font-mono">
                        {usToMs(hop.reverseLatencyUs)}
                    </td>
                    <td
                        className="px-2 py-1.5 text-right font-mono font-semibold"
                        style={{
                            color: getLatencyColor(hop.rttUs / 2),
                        }}
                    >
                        {usToMs(hop.rttUs)}
                    </td>
                    <td
                        className="px-2 py-1.5 text-right font-mono font-semibold"
                        style={{
                            color: getLossColor(hop.lossPercent),
                        }}
                    >
                        {formatLoss(hop.lossPercent)}
                    </td>
                    <td className="px-2 py-1.5 text-right text-semantic-text-secondary">
                        {hop.bandwidth}
                    </td>
                    <td className="px-2 py-1.5 text-right font-mono text-semantic-text-secondary">
                        {formatMbps(linkThroughput.get(hop.linkId) ?? null)}
                    </td>
                </tr>
            ))}
        </tbody>
    </table>
);

export default LatencyPathAnalysis;
